import { useLayoutEffect } from 'react';

/**
 * Marker the forced-light surfaces put on `<html>`. `App` reads it before applying the logged-in
 * user's dark preference, so the two do not fight over the same two attributes.
 */
export const FORCE_LIGHT_ATTRIBUTE = 'data-ot-force-light';

/**
 * Forces the light theme for as long as the calling component is mounted, then restores whatever
 * the app had set.
 *
 * The app-wide dark mode is a preference of the logged-in user, persisted in their browser and
 * defaulted from `REACT_APP_DARK_MODE`. The public apply page has no logged-in user: it is a
 * marketing surface that a stranger opens from a LinkedIn post, usually on a phone, and rendering
 * it dark made a job ad look like an error dialog. A public page picks its own look.
 *
 * Clearing the attributes is not enough on its own: `App` sets them from a passive effect, and
 * React runs every layout effect before any passive effect, so the parent would immediately put
 * `theme="dark"` back. Hence the marker — this hook claims the document in a layout effect, and
 * `App` checks the claim before writing. Both attributes have to go, because the app's own
 * `dark-mode` mixin keys off an ancestor `[theme='dark']` and Bootstrap 5.3 off `data-bs-theme`:
 * either one left behind would restyle the components inside a light page.
 *
 * @userstory US-OPEN-10
 */
export default function useForcedLightTheme(): void {
	useLayoutEffect(() => {
		const root = document.documentElement;
		const previousTheme = root.getAttribute('theme');
		const previousBsTheme = root.getAttribute('data-bs-theme');

		root.setAttribute(FORCE_LIGHT_ATTRIBUTE, 'true');
		root.removeAttribute('theme');
		root.removeAttribute('data-bs-theme');

		return () => {
			root.removeAttribute(FORCE_LIGHT_ATTRIBUTE);
			if (previousTheme) root.setAttribute('theme', previousTheme);
			if (previousBsTheme) root.setAttribute('data-bs-theme', previousBsTheme);
		};
	}, []);
}
